import React, { useEffect, useMemo } from 'react';
import {
  APP_CONTAINER_ROUTES,
  CATALOG_ROUTES,
  ORDER_GUIDE_ROUTES,
} from '@/navigators/routes';
import {
  AppContainerStackParams,
  CatalogStackParams,
  OrderGuideStackParams,
} from '@/navigators/types';
import { BrandPageComponent } from '@/components/brand-page/brand-page-component';
import {
  BrandPageVariantPacksQuery,
  BrandPageVariantPacksQueryVariables,
  ItemsContext_VariantsPackFieldsFragment,
} from '@/types';
import { CompositeNavigationProp, RouteProp } from '@react-navigation/native';
import { gql } from '@apollo/client';
import { StackNavigationProp } from '@react-navigation/stack';
import { useAdaptAndGroupVariantPacks } from '@/hooks/use-adapt-and-group-item-list-options';
import { useAuthenticatedUserContext } from '@/contexts/user-context';
import { useDynamicConfigValue } from '@/hooks/use-dynamic-config-value';
import { useItemQueryLocale } from '@/hooks/use-item-query-variables';
import { useToasts } from '@/contexts/toast-context';
import { useWrappedQuery } from '@/hooks/use-wrapped-query';
import { VARIANTS_PACK_FRAGMENT } from '@/contexts/items-context-queries';

export const BRAND_PAGE_VARIANT_PACKS_QUERY = gql`
  fragment BrandPageFields on brand_pages {
    brand_name
    logo_image_url
    storefront_module
    uuid
  }

  fragment BrandPageVariantPacksFields on GetBrandPageVariantPacksResponse {
    brand_page {
      ...BrandPageFields
    }
    collection_title
    variant_pack {
      ...ItemsContext_VariantsPackFields
    }
  }

  query BrandPageVariantPacks(
    $brandPageUUID: uuid!
    $fulfillmentDate: String
    $locale: String!
    $restaurantUUID: uuid!
    $skipGroupMemberships: Boolean!
    $skipLiveSalt: Boolean!
    $skipSaltApi: Boolean!
    $useSaltCache: Boolean!
    $supplierUUID: uuid!
  ) {
    getBrandPageVariantPacks(
      brand_page_id: $brandPageUUID
      fulfillment_date: $fulfillmentDate
      restaurant_id: $restaurantUUID
      supplier_id: $supplierUUID
    ) {
      ...BrandPageVariantPacksFields
    }
  }

  ${VARIANTS_PACK_FRAGMENT}
`;

interface CollectionVariantPacks {
  [collectionTitle: string]: ItemsContext_VariantsPackFieldsFragment[];
}

export interface Props {
  navigation: CompositeNavigationProp<
    StackNavigationProp<
      AppContainerStackParams,
      APP_CONTAINER_ROUTES.BrandPage
    >,
    CompositeNavigationProp<
      StackNavigationProp<CatalogStackParams, CATALOG_ROUTES.BrandPage>,
      StackNavigationProp<OrderGuideStackParams, ORDER_GUIDE_ROUTES.BrandPage>
    >
  >;
  route:
    | RouteProp<AppContainerStackParams, APP_CONTAINER_ROUTES.BrandPage>
    | RouteProp<CatalogStackParams, CATALOG_ROUTES.BrandPage>
    | RouteProp<OrderGuideStackParams, ORDER_GUIDE_ROUTES.BrandPage>;
}

export const BrandPageScreen: React.FC<Props> = ({ navigation, route }) => {
  const { brandPageUUID } = route.params;
  const { showErrorToast } = useToasts();
  const locale = useItemQueryLocale();
  const adaptAndGroupVariantPacks = useAdaptAndGroupVariantPacks();
  const itemGroupsEnabled = useDynamicConfigValue('item_groups_enabled');
  const saltApiIntegrationEnabled = useDynamicConfigValue(
    'salt_api_integration_enabled',
  );
  const {
    selectedRestaurantRelationship: { supplierUUID },
    selectedRestaurantUUID: restaurantUUID,
  } = useAuthenticatedUserContext();

  const { data, error, loading } = useWrappedQuery<
    BrandPageVariantPacksQuery,
    BrandPageVariantPacksQueryVariables
  >(BRAND_PAGE_VARIANT_PACKS_QUERY, {
    variables: {
      brandPageUUID,
      locale,
      restaurantUUID,
      skipGroupMemberships: !itemGroupsEnabled,
      skipLiveSalt: !saltApiIntegrationEnabled,
      skipSaltApi: !saltApiIntegrationEnabled,
      supplierUUID,
      useSaltCache: false,
    },
  });

  const brandPageContent = data?.getBrandPageVariantPacks[0].brand_page;

  useEffect(() => {
    if (error) {
      showErrorToast();
      navigation.goBack();
    }
  }, [error, navigation, showErrorToast]);

  const [collections, variantPacks] = useMemo(() => {
    const brandDetails = data?.getBrandPageVariantPacks ?? [];
    const [groupedCollections, baseVariantPacks] = brandDetails.reduce<
      [CollectionVariantPacks, ItemsContext_VariantsPackFieldsFragment[]]
    >(
      (acc, bpd) => {
        const [gc, vps] = acc;
        const variantPack = bpd.variant_pack;

        if (variantPack) {
          const collectionTitle = bpd.collection_title;
          if (collectionTitle) {
            if (!gc[collectionTitle]) {
              gc[collectionTitle] = [];
            }

            gc[collectionTitle].push(variantPack);
          } else {
            vps.push(variantPack);
          }
        }

        return [gc, vps];
      },
      [{}, []],
    );

    return [
      Object.entries(groupedCollections).map(([title, vps]) => ({
        title,
        variantPacks: adaptAndGroupVariantPacks(vps),
      })),
      adaptAndGroupVariantPacks(baseVariantPacks),
    ];
  }, [adaptAndGroupVariantPacks, data]);

  return (
    <BrandPageComponent
      brandPageContent={brandPageContent}
      collections={collections}
      loading={loading}
      variantPacks={variantPacks}
    />
  );
};
